import { ref, onMounted, onBeforeUnmount } from "vue";

/**
 * Detecta si hay overlays Ionic abiertos (modal/alert/popover/loading/toast...)
 * sin usar controllers deprecated.
 *
 * Estrategia:
 *  - Busca overlays por tag (ion-modal/ion-alert/...)
 *  - Considera ion-backdrop visible como señal fuerte
 *  - Considera wrappers overlay SOLO si son visibles (evita falsos positivos)
 */
export function useIonicOverlayGuard() {
  const hasOverlayOpen = ref(false);

  const isElementVisible = (el: Element) => {
    const node = el as HTMLElement;
    // offsetParent null a veces falla con position:fixed; usamos rect
    const r = node.getBoundingClientRect?.();
    if (!r) return false;
    const hasBox = r.width > 0 && r.height > 0;
    const style = window.getComputedStyle(node);
    return (
      hasBox &&
      style.display !== "none" &&
      style.visibility !== "hidden" &&
      style.opacity !== "0"
    );
  };

  const compute = () => {
    // overlays “principales”
    const overlaySelectors = [
      "ion-modal",
      "ion-alert",
      "ion-popover",
      "ion-loading",
      "ion-toast",
      // ion-picker está deprecated, pero si existe en tu app aún, lo contemplamos
      "ion-picker",
      // algunos overlays usan wrapper/backdrop internos
      "ion-action-sheet",
    ];

    // 1) Si hay un backdrop visible, casi seguro hay overlay activo
    const backdrop = document.querySelector("ion-backdrop");
    if (backdrop && isElementVisible(backdrop)) {
      hasOverlayOpen.value = true;
      return;
    }

    // 2) Si hay overlay por tag y es visible
    for (const sel of overlaySelectors) {
      const nodes = Array.from(document.querySelectorAll(sel));
      if (nodes.some((n) => isElementVisible(n))) {
        hasOverlayOpen.value = true;
        return;
      }
    }

    // 3) Wrapper genérico: SOLO cuenta si es visible
    const wrappers = Array.from(
      document.querySelectorAll(".ion-overlay-wrapper")
    );
    if (wrappers.some((w) => isElementVisible(w))) {
      hasOverlayOpen.value = true;
      return;
    }

    hasOverlayOpen.value = false;
  };

  let mo: MutationObserver | null = null;
  const onFocus = () => compute();

  onMounted(() => {
    compute();

    mo = new MutationObserver(() => compute());
    mo.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: [
        "class",
        "style",
        "aria-hidden",
        "is-open",
        "presented",
      ],
    });

    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onFocus);
  });

  onBeforeUnmount(() => {
    mo?.disconnect();
    mo = null;
    window.removeEventListener("focus", onFocus);
    document.removeEventListener("visibilitychange", onFocus);
  });

  return { hasOverlayOpen, refreshOverlayState: compute };
}
